import { SubscriptionActionButton } from "@/components/subscription-action-button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { useSubscribeEligibility } from "@/hooks/useSubscribeEligibility";
import { LIBUM_POWERON_PIPELINES_FEATURES_URL } from "@/lib/constants";
import {
  getSubscriptionMetadata,
  SubscriptionProps,
} from "@/types/subscription";
import { ArrowRight, Infinity, LoaderCircle } from "lucide-react";
import { SmoothHeight, useSawLoading } from "@/components/loading-skeletons";
import Link from "next/link";
import { useEffect, useState } from "react";

export default function SubscriptionPowerOnPipelines({
  isAdmin,
  isMatchLoading,
  isSubscribeLoading = false,
  isBillingBusy = false,
  organization,
  subscription,
  apiKeys,
  paymentMethod,
  taxExemption,
  onSubscribe,
  onMatch,
}: SubscriptionProps) {
  const { isSubscribeDisabled, isPendingTaxExemption } =
    useSubscribeEligibility(organization, taxExemption);

  const sawMatchLoading = useSawLoading(isMatchLoading);
  const [keysUsed, setKeysUsed] = useState(0);

  useEffect(() => {
    const pipelineKeys = apiKeys.filter(
      (k) => k.product === "poweron-pipelines",
    );
    setKeysUsed(pipelineKeys.length);
  }, [apiKeys]);

  const getTotalCost = (): string => {
    const metadata = getSubscriptionMetadata(paymentMethod);
    const formattedAmount = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(metadata.powerOnPipelinesPrice);

    return formattedAmount.slice(0, -3);
  };

  return (
    <>
      <div className="grid">
        <Card>
          <CardHeader className="p-4 sm:p-8">
            <CardTitle className="mb-2 w-full text-base sm:text-lg">
              Organizational
            </CardTitle>
            <CardDescription className="text-sm">
              Give your organization{" "}
              <span className="font-bold text-emerald-400">
                version control
              </span>{" "}
              in Symitar with tasks to validate PowerOn files on check-in and
              fully{" "}
              <span className="font-bold text-emerald-400">
                managed releases
              </span>{" "}
              to deploy code to target Syms after the right approvals
            </CardDescription>
          </CardHeader>
          <CardContent className="min-h-[180px] p-4 pt-0 sm:min-h-[202px] sm:p-8 sm:pt-0">
            <div className="mb-6 sm:mb-8">
              <span className="text-2xl font-bold text-gray-1100 dark:text-gray-100 sm:text-4xl">
                {new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                })
                  .format(
                    getSubscriptionMetadata(paymentMethod)
                      .powerOnPipelinesPrice || 0,
                  )
                  .slice(0, -3)}
              </span>
              <span className="ml-1 text-base text-muted sm:ml-1.5 sm:text-xl">
                /{" "}
                {paymentMethod === "monthly"
                  ? "month"
                  : paymentMethod === "yearly"
                    ? "year"
                    : "3-years"}
              </span>
            </div>
            <Separator />
            {subscription && subscription.seats > 0 ? (
              <div className="mt-4 flex flex-row gap-2">
                <div className="flex w-full flex-col pb-1.5">
                  <div className="flex flex-wrap items-baseline justify-between gap-1">
                    <h2 className="text-base font-medium text-gray-1100 dark:text-gray-100 sm:text-lg">
                      API Keys
                    </h2>
                    <span className="flex flex-wrap items-center text-xs font-medium text-muted sm:text-sm">
                      <span className="mr-1 font-bold text-gray-1100 dark:text-gray-100">
                        {keysUsed}
                      </span>
                      of
                      <span className="mx-1 flex items-center font-bold text-emerald-400">
                        <Infinity className="mr-0.5 size-4 sm:mr-1 sm:size-5" />
                        <span className="hidden sm:inline">keys</span>
                      </span>
                      used
                    </span>
                  </div>
                  <Progress value={100} className="mt-2.5" />
                  <Link
                    href="/settings/api-keys"
                    className="mt-2 text-xs font-medium text-brand-accent hover:underline sm:text-sm"
                  >
                    Create secret keys
                  </Link>
                </div>
                <div className="flex shrink-0 items-center">
                  <span className="text-sm font-bold text-gray-1100 dark:text-gray-100 sm:ml-2 sm:text-base">
                    {getTotalCost()}
                  </span>
                </div>
              </div>
            ) : (
              <div className="mt-5 flex flex-col sm:mt-7">
                <SubscriptionActionButton
                  loading={isSubscribeLoading}
                  wrapperClassName="w-full"
                  className="w-full"
                  size="lg"
                  onClick={() => onSubscribe()}
                  disabled={isSubscribeDisabled || !isAdmin || isBillingBusy}
                >
                  Subscribe now
                </SubscriptionActionButton>
                <SmoothHeight className="flex flex-col">
                  {isMatchLoading ? (
                    <span
                      role="status"
                      className="mt-1 flex items-center gap-1.5 text-xs text-muted"
                    >
                      <LoaderCircle
                        aria-hidden="true"
                        className="size-3.5 animate-spin text-brand-primary"
                      />
                      Matching subscription…
                    </span>
                  ) : isPendingTaxExemption ? (
                    <span
                      className={
                        sawMatchLoading
                          ? "mt-1 text-xs text-muted duration-300 animate-in fade-in"
                          : "mt-1 text-xs text-muted"
                      }
                    >
                      Pending review of sales tax exemption. See{" "}
                      <Link
                        className="text-brand-accent"
                        href="/settings/sales-tax"
                      >
                        Sales Tax
                      </Link>{" "}
                      page for status.
                    </span>
                  ) : isAdmin ? (
                    <span
                      className={
                        sawMatchLoading
                          ? "mt-1 text-xs text-muted duration-300 animate-in fade-in"
                          : "mt-1 text-xs text-muted"
                      }
                    >
                      Already have a subscription?
                      <span
                        className="ml-1 cursor-pointer text-brand-accent hover:underline"
                        onClick={onMatch}
                      >
                        Click here
                      </span>{" "}
                      to try and match by email.
                    </span>
                  ) : (
                    <span
                      className={
                        sawMatchLoading
                          ? "mt-1 text-xs text-muted duration-300 animate-in fade-in"
                          : "mt-1 text-xs text-muted"
                      }
                    >
                      You must be the organization owner or an admin to
                      subscribe.
                    </span>
                  )}
                </SmoothHeight>
              </div>
            )}
          </CardContent>
          <CardFooter
            className="cursor-pointer justify-center rounded-b-md border-t px-4 py-4 text-sm font-medium transition-colors duration-200 hover:bg-sidebar-accent hover:text-gray-1100 hover:dark:text-gray-100 sm:px-6 sm:py-6 sm:text-lg"
            onClick={() => {
              window.open(
                LIBUM_POWERON_PIPELINES_FEATURES_URL,
                "_blank",
                "noopener,noreferrer",
              );
            }}
          >
            <span className="sm:hidden">Features & use cases</span>
            <span className="hidden sm:inline">
              Check out features & use cases
            </span>
            <ArrowRight className="ml-1 mt-0.5 size-4 text-muted" />
          </CardFooter>
        </Card>
      </div>
    </>
  );
}
